import { useEffect, useState, useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { usePortfolio } from '../../context/PortfolioContext';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

function CashHistoryChart() {
  const { cashHistory, loadCashHistory } = usePortfolio();

  useEffect(() => {
    // Only load if no data exists to prevent loading on navigation
    if (!cashHistory) {
      loadCashHistory();
    }
  }, [cashHistory, loadCashHistory]);

  const chartData = useMemo(() => {
    if (!cashHistory?.points?.length) return null;

    const points = cashHistory.points;
    const labels = points.map(p => {
      const date = new Date(p.date);
      return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear().toString().substr(2)}`;
    });

    const cashValues = points.map(p => p.cash_gbp);

    return {
      labels,
      datasets: [
        {
          label: 'Cash Available (GBP)',
          data: cashValues,
          borderColor: '#22c55e', // green-500
          backgroundColor: (context) => {
            const {ctx, chartArea} = context.chart;
            if (!chartArea) return 'rgba(34, 197, 94, 0.1)';
            
            const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
            const zeroY = context.chart.scales.y.getPixelForValue(0);
            const topRatio = Math.max(0, Math.min(1, (zeroY - chartArea.top) / (chartArea.bottom - chartArea.top)));
            
            // Green above zero, red below
            gradient.addColorStop(0, 'rgba(34, 197, 94, 0.3)');
            gradient.addColorStop(topRatio, 'rgba(34, 197, 94, 0.1)');
            gradient.addColorStop(Math.min(topRatio + 0.01, 1), 'rgba(239, 68, 68, 0.1)');
            gradient.addColorStop(1, 'rgba(239, 68, 68, 0.3)');
            
            return gradient;
          },
          fill: 'origin',
          tension: 0.35,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
          pointBackgroundColor: '#22c55e',
          segment: {
            borderColor: ctx => {
              const val = ctx.p1.parsed.y;
              return val >= 0 ? '#22c55e' : '#ef4444';
            }
          }
        },
        // Zero line
        {
          label: 'Zero',
          data: points.map(() => 0),
          borderColor: 'rgba(127, 140, 160, 0.5)',
          borderWidth: 1,
          pointRadius: 0,
          fill: false,
          tension: 0,
        },
      ],
    };
  }, [cashHistory]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false, // Hide legend for cleaner look
      },
      title: {
        display: true,
        text: 'Cash Available Over Time',
        color: '#dff0ff',
        font: { family: 'Space Grotesk', size: 14, weight: '600' },
        padding: { top: 10, bottom: 20 },
      },
      tooltip: {
        backgroundColor: 'rgba(12, 28, 48, 0.96)',
        borderColor: 'rgba(34, 197, 94, 0.35)',
        borderWidth: 1,
        titleColor: '#dff0ff',
        bodyColor: '#dff0ff',
        padding: 10,
        callbacks: {
          label: (context) => {
            const value = context.parsed.y;
            return `${context.dataset.label}: ${new Intl.NumberFormat('en-GB', {
              style: 'currency',
              currency: 'GBP',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }).format(value)}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          color: 'rgba(29, 56, 85, 0.25)',
          drawBorder: false,
        },
        ticks: {
          color: '#7a99b8',
          font: { family: 'Space Mono', size: 9 },
          maxTicksLimit: 6,
        },
      },
      y: {
        grid: {
          color: 'rgba(29, 56, 85, 0.25)',
          drawBorder: false,
        },
        ticks: {
          color: '#7a99b8',
          font: { family: 'Space Mono', size: 10 },
          callback: (value) => {
            return new Intl.NumberFormat('en-GB', {
              style: 'currency',
              currency: 'GBP',
              notation: 'compact',
              maximumFractionDigits: 1,
            }).format(value);
          },
        },
      },
    },
    interaction: {
      intersect: false,
      mode: 'index',
    },
  };

  if (!cashHistory) {
    return (
      <div className="chart-container">
        <div style={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span className="text-muted">Loading chart...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="chart-container">
      <div className="chart-wrapper" style={{ height: '250px' }}>
        {chartData ? (
          <Line data={chartData} options={options} />
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <span className="text-muted">No cash data available</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default CashHistoryChart;
